// bluaIQ — PROJECT-MATERIAL-LIST-1: hidden demo shell for the Project Material List.
//
// DEMO / READ-ONLY ONLY. Renders the static demo fixture (typed against the
// PROJECT-MATERIAL-LIST-0 contracts) as a read-only preview of the intermediate
// Project Material List layer. It performs NO network I/O (no fetch/apiFetch), makes
// NO POST/PATCH/PUT/DELETE, writes nothing, and reaches no downstream module. Every
// downstream card below is locked/read-only or proposal-only — human approval is
// required before anything could ever be written, and that wiring does not exist.
// This shell does NOT connect to any Takeoff implementation. Bilingual copy is inline.

import { useTranslation } from 'react-i18next'
import {
  projectMaterialListDemoItems,
  projectMaterialListDemoNoSilentDrop,
  projectMaterialListDemoQuoteCoverageSummary,
} from './projectMaterialListDemoFixture'
import {
  takeoffPmlPreviewItems,
  takeoffPmlPreviewNoSilentDrop,
  TAKEOFF_PML_PREVIEW_SOURCE_DOCUMENT_HASH,
} from './takeoffPmlPreviewFixture'
import type { TakeoffPreviewLinkageStatus, TakeoffPreviewQuantityStatus } from './takeoffPmlPreviewFixture'

const COPY = {
  title: { en: 'Project Material List', es: 'Lista Maestra de Materiales' },
  masterTitle: { en: 'Master Material List', es: 'Lista Maestra de Materiales' },
  demoData: { en: 'Demo Data', es: 'Datos demo' },
  readOnlyPreview: { en: 'Read-only preview', es: 'Vista previa de solo lectura' },
  notPurchaseReady: { en: 'Not purchase-ready', es: 'No está listo para compra' },
  notBomBoq: { en: 'Not a BOM or BOQ', es: 'No es BOM ni BOQ' },
  humanApprovalRequired: {
    en: 'Human approval required before downstream writes',
    es: 'Se requiere aprobación humana antes de crear datos en otros módulos',
  },
  sourceProvenanceRequired: { en: 'Source provenance required', es: 'Se requiere procedencia de origen' },
  needsReview: { en: 'Needs review', es: 'Requiere revisión' },
  materialList: { en: 'Material list', es: 'Lista de materiales' },
  source: { en: 'Source', es: 'Origen' },
  quantityStatus: { en: 'Quantity status', es: 'Estado de cantidad' },
  confidence: { en: 'Confidence', es: 'Confianza' },
  assumption: { en: 'Assumption', es: 'Supuesto' },
  blocker: { en: 'Blocker', es: 'Bloqueo' },
  reviewReason: { en: 'Review reason', es: 'Motivo de revisión' },
  noSilentDrop: { en: 'No silent drop', es: 'Sin descartes silenciosos' },
  retained: { en: 'retained', es: 'conservados' },
  dropped: { en: 'dropped', es: 'descartados' },
  downstream: { en: 'Downstream proposals (locked / read-only)', es: 'Propuestas a otros módulos (bloqueadas / solo lectura)' },
  locked: { en: 'Locked — read only', es: 'Bloqueado — solo lectura' },
  proposalOnly: { en: 'Proposal only', es: 'Solo propuesta' },
  requiresApproval: { en: 'Requires human approval', es: 'Requiere aprobación humana' },
  noWrites: { en: 'No downstream records are created', es: 'No crea registros en otros módulos' },
  // Downstream card titles.
  cardQuotes: { en: 'Quotes coverage', es: 'Cobertura de cotizaciones' },
  cardBudget: { en: 'Budget draft', es: 'Borrador de presupuesto' },
  cardRfq: { en: 'RFQ package', es: 'Paquete RFQ' },
  cardContract: { en: 'Contract draft', es: 'Borrador de contrato' },
  cardSchedule: { en: 'Payment schedule draft', es: 'Borrador de calendario de pagos' },
  cardGantt: { en: 'Gantt proposal', es: 'Propuesta de Gantt' },
  cardCalendar: { en: 'Calendar proposal', es: 'Propuesta de calendario' },
  cardDashboard: { en: 'Dashboard read model', es: 'Modelo de lectura del panel' },
  cardAiChat: { en: 'AI Chat read-only context', es: 'Contexto de solo lectura del chat IA' },
  // Source-module labels.
  srcTakeoffCurrent: { en: 'Current Takeoff', es: 'Takeoff actual' },
  srcTakeoffV2: { en: 'Verified Takeoff', es: 'Takeoff verificado' },
  srcAiDraftTakeoff: { en: 'AI Draft Takeoff', es: 'Cuantificación preliminar IA' },
  srcManual: { en: 'Manual entry', es: 'Entrada manual' },
  srcCsv: { en: 'CSV import', es: 'Importación CSV' },
  srcPdf: { en: 'PDF import', es: 'Importación PDF' },
  srcMaterialOrder: { en: 'Material Order', es: 'Orden de material' },
  // Quantity-status labels.
  statusDraft: { en: 'Draft', es: 'Borrador' },
  statusNeedsReview: { en: 'Needs review', es: 'Requiere revisión' },
  statusVerified: { en: 'Verified', es: 'Verificado' },
  statusBlocked: { en: 'Blocked', es: 'Bloqueado' },
} as const

const DOWNSTREAM_CARDS = [
  'cardQuotes', 'cardBudget', 'cardRfq', 'cardContract', 'cardSchedule',
  'cardGantt', 'cardCalendar', 'cardDashboard', 'cardAiChat',
] as const

const SOURCE_LABEL_KEY: Record<string, keyof typeof COPY> = {
  takeoff_current: 'srcTakeoffCurrent',
  takeoff_v2: 'srcTakeoffV2',
  ai_draft_takeoff: 'srcAiDraftTakeoff',
  manual: 'srcManual',
  csv: 'srcCsv',
  pdf: 'srcPdf',
  material_order: 'srcMaterialOrder',
}

const STATUS_LABEL_KEY: Record<string, keyof typeof COPY> = {
  draft: 'statusDraft',
  needs_review: 'statusNeedsReview',
  verified: 'statusVerified',
  blocked: 'statusBlocked',
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  needs_review: 'bg-amber-50 text-amber-800',
  verified: 'bg-green-50 text-green-700',
  blocked: 'bg-red-50 text-red-700',
}

// ── WEBSITE-TAKEOFF-PML-PREVIEW-0: bilingual copy for the read-only Takeoff evidence
// preview (mirrors the accepted TAKEOFF artifacts). Inline like COPY above — no t() so
// the whole section stays a fixed, read-only preview.
const TCOPY = {
  title: { en: 'Takeoff evidence preview', es: 'Vista previa de evidencia de cuantificación' },
  readOnlyPreview: {
    en: 'Read-only Takeoff to Project Material List preview',
    es: 'Vista previa de solo lectura de cuantificación a Lista Maestra de Materiales',
  },
  acceptedArtifactPreview: {
    en: 'Accepted TAKEOFF artifact preview',
    es: 'Vista previa de artifacts aceptados de TAKEOFF',
  },
  notVerified: { en: 'Not verified', es: 'No verificado' },
  quantityNotAvailable: { en: 'Quantity not available', es: 'Cantidad no disponible' },
  notReadyToOrder: { en: 'Not ready to order', es: 'No está listo para compra' },
  noBomBoqPurchaseMaterialOrder: {
    en: 'No BOM, BOQ, purchase quantity, or Material Order is created',
    es: 'No se crea BOM, BOQ, cantidad de compra ni orden de materiales',
  },
  humanReviewRequired: {
    en: 'Human review is required before any downstream write',
    es: 'Se requiere revisión humana antes de crear datos en otros módulos',
  },
  sourceHash: { en: 'Source document hash', es: 'Hash del documento de origen' },
  record: { en: 'Record', es: 'Registro' },
  reviewReason: { en: 'Review reason', es: 'Motivo de revisión' },
  assumption: { en: 'Assumption', es: 'Supuesto' },
  quantityDash: { en: 'Quantity —', es: 'Cantidad —' },
  statusNeedsReview: { en: 'Needs review', es: 'Requiere revisión' },
  statusBlocked: { en: 'Blocked', es: 'Bloqueado' },
  recoveredPhrase: { en: 'Recovered phrase', es: 'Frase recuperada' },
  spacingContext: {
    en: 'Spacing context only — not a quantity, not a confirmed dimension',
    es: 'Solo contexto de espaciamiento — no es una cantidad ni una dimensión confirmada',
  },
  weakNegativeLinkage: {
    en: 'Weak / negative linkage for furring — not a run length, not a quantity basis',
    es: 'Vinculación débil / negativa para furring — no es una longitud ni una base de cantidad',
  },
  noSilentDrop: { en: 'No silent drop', es: 'Sin descartes silenciosos' },
  dropped: { en: 'dropped', es: 'descartados' },
} as const

const TLINKAGE_LABEL: Record<TakeoffPreviewLinkageStatus, { en: string; es: string }> = {
  assisted_capture_pending: { en: 'Assisted capture pending', es: 'Captura asistida pendiente' },
  opening_dimension_linkage_gated: {
    en: 'Opening-dimension linkage gated',
    es: 'Vinculación de dimensión de abertura bloqueada',
  },
  unresolved_no_dimension_candidate_recovered: {
    en: 'Unresolved — no dimension candidate',
    es: 'Sin resolver — sin candidato de dimensión',
  },
  linkage_evidence_expanded_review_required: {
    en: 'Linkage evidence expanded — review required',
    es: 'Evidencia de vinculación ampliada — revisión requerida',
  },
  assumption_requires_review: {
    en: 'Assumption requires review',
    es: 'El supuesto requiere revisión',
  },
}

const TSTATUS_BADGE: Record<TakeoffPreviewQuantityStatus, string> = {
  needs_review: 'bg-amber-50 text-amber-800',
  blocked: 'bg-red-50 text-red-700',
}

export default function ProjectMaterialListDemoShell() {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)

  const DemoBadge = () => (
    <span
      data-testid="pml-demo-badge"
      className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[9px] font-[900] uppercase tracking-wide px-2 py-0.5 whitespace-nowrap"
    >
      {L(COPY.demoData)}
    </span>
  )

  return (
    <div data-testid="pml-demo-shell" className="p-4 sm:p-5 max-w-5xl mx-auto min-w-0 space-y-4">
      {/* Top warning banner */}
      <div
        data-testid="pml-warning-banner"
        className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 flex flex-wrap items-start gap-2 min-w-0"
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[15px] font-[900] text-amber-900 break-words">{L(COPY.title)}</h1>
            <DemoBadge />
          </div>
          <p className="text-[11px] font-[800] text-amber-800 mt-0.5 break-words">{L(COPY.masterTitle)}</p>
          <ul className="mt-1.5 space-y-0.5 text-[11px] font-[700] text-amber-900">
            <li data-testid="pml-read-only-preview">• {L(COPY.readOnlyPreview)}</li>
            <li data-testid="pml-not-purchase-ready">• {L(COPY.notPurchaseReady)}</li>
            <li data-testid="pml-not-bom-boq">• {L(COPY.notBomBoq)}</li>
            <li data-testid="pml-human-approval">• {L(COPY.humanApprovalRequired)}</li>
            <li data-testid="pml-source-provenance-required">• {L(COPY.sourceProvenanceRequired)}</li>
          </ul>
        </div>
      </div>

      {/* Material list — cards on mobile, responsive grid on desktop. */}
      <section data-testid="pml-material-list" className="space-y-3">
        <h2 className="text-[12px] font-[900] uppercase tracking-wide text-[#9E9890] flex flex-wrap items-center gap-2">
          {L(COPY.materialList)} <DemoBadge />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {projectMaterialListDemoItems.map((item) => {
            const sourceKey = SOURCE_LABEL_KEY[item.sourceModule]
            const statusKey = STATUS_LABEL_KEY[item.quantityStatus]
            return (
              <div
                key={item.id}
                data-testid={`pml-item-${item.id}`}
                className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-[12px] font-[900] text-[#1C1A17] break-words min-w-0 flex-1">{item.itemName}</h3>
                  <span className="text-[11px] font-[900] text-[#1C1A17] whitespace-nowrap">
                    {item.quantity ?? '—'} {item.unit ?? ''}
                  </span>
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {/* Source badge */}
                  <span
                    data-testid="pml-source-badge"
                    className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
                  >
                    {L(COPY.source)}: {sourceKey ? L(COPY[sourceKey]) : item.sourceModule}
                  </span>
                  {/* Quantity-status badge */}
                  <span
                    data-testid="pml-quantity-status-badge"
                    className={`inline-flex items-center rounded-full text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap ${STATUS_BADGE_CLASS[item.quantityStatus] ?? 'bg-gray-100 text-gray-700'}`}
                  >
                    {statusKey ? L(COPY[statusKey]) : item.quantityStatus}
                  </span>
                  {/* Confidence badge */}
                  <span
                    data-testid="pml-confidence-badge"
                    className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
                  >
                    {L(COPY.confidence)} {Math.round(item.confidence * 100)}%
                  </span>
                  {/* Needs-review badge */}
                  {item.needsReview && (
                    <span
                      data-testid="pml-needs-review-badge"
                      className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 text-[9px] font-[900] px-2 py-0.5 whitespace-nowrap"
                    >
                      {L(COPY.needsReview)}
                    </span>
                  )}
                </div>

                {/* Provenance */}
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 min-w-0">
                  <span className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">{L(COPY.source)}:</span>
                  {item.provenanceRefs.map((ref, idx) => (
                    <span
                      key={`${item.id}-prov-${idx}`}
                      data-testid="pml-provenance-ref"
                      className="inline-flex items-center rounded-full bg-gray-50 text-gray-600 text-[9px] font-[700] px-2 py-0.5 max-w-full break-words"
                    >
                      {ref.sourceId}{ref.sourceLineId ? ` / ${ref.sourceLineId}` : ''}
                    </span>
                  ))}
                </div>

                {/* Assumptions */}
                {item.assumptions.length > 0 && (
                  <div className="mt-1.5 flex flex-col gap-1">
                    {item.assumptions.map((a) => (
                      <span
                        key={a.id}
                        data-testid="pml-assumption-badge"
                        className="inline-flex items-start rounded-md bg-purple-50 text-purple-700 text-[10px] font-[700] px-2 py-1 break-words min-w-0"
                      >
                        {L(COPY.assumption)}: {a.statement}
                      </span>
                    ))}
                  </div>
                )}

                {/* Blockers */}
                {item.blockers.length > 0 && (
                  <div className="mt-1.5 flex flex-col gap-1">
                    {item.blockers.map((b) => (
                      <span
                        key={b.code}
                        data-testid="pml-blocker-badge"
                        className={`inline-flex items-start rounded-md text-[10px] font-[800] px-2 py-1 break-words min-w-0 ${
                          b.isBlocking ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                        }`}
                      >
                        {b.severity}: {b.message}
                      </span>
                    ))}
                  </div>
                )}

                {/* Review reason */}
                {item.reviewReason && (
                  <p data-testid="pml-review-reason" className="mt-1.5 text-[10px] font-[700] text-[#6B655C] break-words min-w-0">
                    {L(COPY.reviewReason)}: {item.reviewReason}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* No-silent-drop summary */}
      <section data-testid="pml-no-silent-drop" className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
        <h2 className="text-[11px] font-[900] uppercase tracking-wide text-[#9E9890] mb-1.5">{L(COPY.noSilentDrop)}</h2>
        <p className="text-[10px] font-[800] text-[#6B655C]">
          {projectMaterialListDemoNoSilentDrop.retainedItems} {L(COPY.retained)} · {projectMaterialListDemoNoSilentDrop.droppedItems.length} {L(COPY.dropped)} / {projectMaterialListDemoNoSilentDrop.totalSourceItems}
        </p>
        <div className="mt-1.5 space-y-1">
          {projectMaterialListDemoNoSilentDrop.droppedItems.map((d) => (
            <div key={d.upstreamId} data-testid="pml-dropped-item" className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0">
              • <span className="font-[900]">{d.upstreamId}</span> — {d.reason}
            </div>
          ))}
        </div>
      </section>

      {/* Locked / read-only / proposal-only downstream cards */}
      <section data-testid="pml-downstream" className="space-y-2">
        <h2 className="text-[12px] font-[900] uppercase tracking-wide text-[#9E9890] flex flex-wrap items-center gap-2">
          {L(COPY.downstream)} <DemoBadge />
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {DOWNSTREAM_CARDS.map((key) => (
            <div
              key={key}
              data-testid={`pml-downstream-card-${key}`}
              aria-disabled="true"
              className="rounded-lg border border-[#D6CFC4] bg-[#F3F1EC] p-3 opacity-70 cursor-not-allowed min-w-0"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-[11px] font-[900] text-[#6B655C] break-words min-w-0">{L(COPY[key])}</h3>
                <span
                  data-testid="pml-locked-badge"
                  className="inline-flex items-center rounded-full bg-[#E4DFD6] text-[#6B655C] text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
                >
                  🔒 {L(COPY.locked)}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap">
                  {L(COPY.proposalOnly)}
                </span>
                <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap">
                  {L(COPY.requiresApproval)}
                </span>
              </div>
              <p className="mt-1 text-[10px] font-[700] text-[#9E9890] break-words">{L(COPY.noWrites)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quote coverage summary demo (illustrates a read-only downstream shape) */}
      <section data-testid="pml-quote-coverage-summary" className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
        <h2 className="text-[11px] font-[900] uppercase tracking-wide text-[#9E9890] mb-1.5">{L(COPY.cardQuotes)}</h2>
        <p className="text-[10px] font-[700] text-[#6B655C]">
          {projectMaterialListDemoQuoteCoverageSummary.quotedItems} / {projectMaterialListDemoQuoteCoverageSummary.totalMaterialItems} · {projectMaterialListDemoQuoteCoverageSummary.missingItems.length} missing · {projectMaterialListDemoQuoteCoverageSummary.needsReviewItems} {L(COPY.needsReview).toLowerCase()}
        </p>
      </section>

      {/* ── WEBSITE-TAKEOFF-PML-PREVIEW-0: read-only Takeoff evidence preview ──────────
          Mirrors the accepted TAKEOFF artifacts. Read-only: nothing verified, no
          quantity, not ready to order, no BOM/BOQ/purchase quantity/Material Order,
          human review required before any downstream write. */}
      <section data-testid="takeoff-evidence-preview" className="space-y-3">
        <div
          data-testid="takeoff-preview-banner"
          className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 min-w-0"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[13px] font-[900] text-amber-900 break-words">{L(TCOPY.title)}</h2>
            <DemoBadge />
          </div>
          <ul className="mt-1.5 space-y-0.5 text-[11px] font-[700] text-amber-900">
            <li data-testid="takeoff-preview-readonly">• {L(TCOPY.readOnlyPreview)}</li>
            <li data-testid="takeoff-preview-accepted">• {L(TCOPY.acceptedArtifactPreview)}</li>
            <li data-testid="takeoff-preview-not-verified">• {L(TCOPY.notVerified)}</li>
            <li data-testid="takeoff-preview-qty-na">• {L(TCOPY.quantityNotAvailable)}</li>
            <li data-testid="takeoff-preview-not-ready">• {L(TCOPY.notReadyToOrder)}</li>
            <li data-testid="takeoff-preview-no-bom">• {L(TCOPY.noBomBoqPurchaseMaterialOrder)}</li>
            <li data-testid="takeoff-preview-human-review">• {L(TCOPY.humanReviewRequired)}</li>
          </ul>
          <p className="mt-1.5 text-[9px] font-[800] uppercase tracking-wide text-amber-800 break-words min-w-0">
            {L(TCOPY.sourceHash)}: {TAKEOFF_PML_PREVIEW_SOURCE_DOCUMENT_HASH}
          </p>
        </div>

        {/* Record cards — stacked on mobile, responsive grid on desktop. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {takeoffPmlPreviewItems.map((item) => (
            <div
              key={item.recordId}
              data-testid={`takeoff-preview-item-${item.recordId}`}
              className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-[12px] font-[900] text-[#1C1A17] break-words min-w-0 flex-1">{item.itemName}</h3>
                <span className="text-[11px] font-[900] text-[#B4453C] whitespace-nowrap">{L(TCOPY.quantityDash)}</span>
              </div>
              <p className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0">{item.stage}</p>

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span
                  data-testid="takeoff-preview-status-badge"
                  className={`inline-flex items-center rounded-full text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap ${TSTATUS_BADGE[item.quantityStatus]}`}
                >
                  {L(item.quantityStatus === 'blocked' ? TCOPY.statusBlocked : TCOPY.statusNeedsReview)}
                </span>
                <span
                  data-testid="takeoff-preview-linkage-badge"
                  className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-[9px] font-[800] px-2 py-0.5 max-w-full break-words"
                >
                  {L(TLINKAGE_LABEL[item.linkageStatus])}
                </span>
                <span
                  data-testid="takeoff-preview-not-verified-badge"
                  className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
                >
                  {L(TCOPY.notVerified)}
                </span>
                <span
                  data-testid="takeoff-preview-not-ready-badge"
                  className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
                >
                  {L(TCOPY.notReadyToOrder)}
                </span>
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 min-w-0">
                <span className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">{L(TCOPY.record)}:</span>
                <span data-testid="takeoff-preview-record-id" className="text-[9px] font-[700] text-[#6B655C] break-words max-w-full">{item.recordId}</span>
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-1 min-w-0">
                {item.provenanceRefs.map((ref, idx) => (
                  <span
                    key={`${item.recordId}-ref-${idx}`}
                    data-testid="takeoff-preview-provenance-ref"
                    className="inline-flex items-center rounded-full bg-gray-50 text-gray-600 text-[9px] font-[700] px-2 py-0.5 max-w-full break-words"
                  >
                    {ref.artifactRef}
                  </span>
                ))}
              </div>

              {item.blockers.length > 0 && (
                <div className="mt-1.5 flex flex-col gap-1">
                  {item.blockers.map((b) => (
                    <span
                      key={b.code}
                      data-testid="takeoff-preview-blocker"
                      className={`inline-flex items-start rounded-md text-[10px] font-[800] px-2 py-1 break-words min-w-0 ${
                        b.isBlocking ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                      }`}
                    >
                      {b.severity}: {b.message}
                    </span>
                  ))}
                </div>
              )}

              {item.assumptions.length > 0 && (
                <div className="mt-1.5 flex flex-col gap-1">
                  {item.assumptions.map((a) => (
                    <span
                      key={a.id}
                      data-testid="takeoff-preview-assumption"
                      className="inline-flex items-start rounded-md bg-purple-50 text-purple-700 text-[10px] font-[700] px-2 py-1 break-words min-w-0"
                    >
                      {L(TCOPY.assumption)}: {a.statement}
                    </span>
                  ))}
                </div>
              )}

              <p data-testid="takeoff-preview-review-reason" className="mt-1.5 text-[10px] font-[700] text-[#6B655C] break-words min-w-0">
                {L(TCOPY.reviewReason)}: {L(item.reviewReason)}
              </p>

              {/* P2 dimension-linkage evidence (record 0011 only) — nothing confirmed. */}
              {item.p2Evidence && (
                <div data-testid="takeoff-preview-p2-evidence" className="mt-2 rounded-md border border-[#E4EDF6] bg-[#F7FAFD] p-2 min-w-0">
                  <p className="text-[10px] font-[900] text-[#1C1A17] break-words min-w-0">
                    {L(TCOPY.recoveredPhrase)}:{' '}
                    <span data-testid="takeoff-preview-recovered-phrase" className="font-[800] break-words">{item.p2Evidence.recoveredPhrase}</span>
                  </p>
                  <div className="mt-1 space-y-1">
                    {item.p2Evidence.tokens.map((tk) => (
                      <div
                        key={tk.token}
                        data-testid="takeoff-preview-token"
                        className="flex flex-col gap-0.5 rounded bg-white border border-[#E4EDF6] px-2 py-1 min-w-0"
                      >
                        <span className="text-[10px] font-[800] text-[#1C1A17] break-words min-w-0">
                          <span className="font-[900]">{tk.token}</span>{' '}
                          <span
                            data-testid={
                              tk.classification === 'spacing_context_candidate'
                                ? 'takeoff-preview-token-spacing'
                                : 'takeoff-preview-token-weak-negative'
                            }
                          >
                            — {L(tk.classification === 'spacing_context_candidate' ? TCOPY.spacingContext : TCOPY.weakNegativeLinkage)}
                          </span>
                        </span>
                        <span className="text-[9px] font-[700] text-[#6B655C] break-words min-w-0">{L(tk.note)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* No-silent-drop proof for the preview — all 15 records represented, none dropped. */}
        <div data-testid="takeoff-preview-no-silent-drop" className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
          <p className="text-[10px] font-[800] text-[#6B655C] break-words min-w-0">
            {L(TCOPY.noSilentDrop)}: {takeoffPmlPreviewNoSilentDrop.representedRecords} / {takeoffPmlPreviewNoSilentDrop.totalRecords} · {takeoffPmlPreviewNoSilentDrop.droppedRecords.length} {L(TCOPY.dropped)}
          </p>
        </div>
      </section>
    </div>
  )
}
